import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Check,
  Copy,
  Download,
  KeyRound,
  Loader2,
  ShieldCheck,
  ShieldOff,
  ShieldQuestion,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * Phase H — Two-factor authentication card.
 *
 * Three states:
 *   1) Disabled  → "Enable 2FA" button → opens setup dialog
 *      Setup dialog steps: secret + QR → user types first OTP → recovery codes
 *   2) Enabled   → "Disable" button (requires password + current OTP)
 *
 * Works for both platform admins AND tenant users — the backend resolves
 * the subject from the JWT.
 */
export function TwoFactorCard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup flow state
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState("scan"); // scan | confirm | recovery
  const [secret, setSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  // Disable flow
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePass, setDisablePass] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const reload = async () => {
    try {
      setLoading(true);
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSecret(r.data.secret);
      setOtpauthUri(r.data.otpauth_uri);
      setSetupStep("scan");
      setConfirmCode("");
      setRecoveryCodes([]);
      setSetupOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("twofa.setupFailed", "Could not start 2FA setup"));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify-setup", { code: confirmCode.trim() });
      setRecoveryCodes(r.data.recovery_codes || []);
      setSetupStep("recovery");
      toast.success(t("twofa.enabled", "Two-factor authentication enabled"));
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("twofa.confirmFailed", "Could not verify code"));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        password: disablePass,
        code: disableCode.trim(),
      });
      toast.success(t("twofa.disabled", "Two-factor authentication disabled"));
      setDisableOpen(false);
      setDisablePass("");
      setDisableCode("");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("twofa.disableFailed", "Could not disable 2FA"));
    } finally {
      setBusy(false);
    }
  };

  const copyAllCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success(t("twofa.copied", "Recovery codes copied to clipboard"));
    } catch {
      toast.error(t("twofa.copyFailed", "Could not copy"));
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [
        "Elswitch — 2FA recovery codes",
        "Generated " + new Date().toISOString(),
        "",
        "Each code can be used ONCE in place of a TOTP code if you lose your authenticator.",
        "Keep these somewhere safe (password manager, printed copy).",
        "",
        ...recoveryCodes,
      ].join("\n"),
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "elswitch-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-5 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("twofa.loading", "Checking 2FA status…")}
        </CardContent>
      </Card>
    );
  }
  if (!status) return null;

  return (
    <>
      <Card data-testid="card-twofa">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-primary/10 p-2 mt-0.5">
              {status.enabled ? (
                <ShieldCheck className="h-5 w-5 text-[hsl(var(--ok))]" />
              ) : (
                <ShieldQuestion className="h-5 w-5 text-[hsl(var(--warn))]" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold">
                  {t("twofa.title", "Two-factor authentication")}
                </h3>
                {status.enabled ? (
                  <Badge variant="outline" className="border-[hsl(var(--ok))] text-[hsl(var(--ok))]">
                    {t("twofa.statusOn", "Enabled")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    {t("twofa.statusOff", "Off")}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {status.enabled
                  ? t(
                      "twofa.descOn",
                      "Sign-in requires a 6-digit code from your authenticator app. {{n}} recovery codes remaining.",
                      { n: status.recovery_codes_remaining }
                    )
                  : t(
                      "twofa.descOff",
                      "Add an extra layer of protection to your account. Use Google Authenticator, 1Password, Bitwarden, or any TOTP app."
                    )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {!status.enabled ? (
              <Button onClick={startEnroll} disabled={busy} data-testid="twofa-enable-btn" className="gap-1">
                <ShieldCheck className="h-4 w-4" />
                {t("twofa.enableBtn", "Enable 2FA")}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setDisableOpen(true)}
                data-testid="twofa-disable-btn"
                className="gap-1 border-[hsl(var(--danger))] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))/0.08]"
              >
                <ShieldOff className="h-4 w-4" />
                {t("twofa.disableBtn", "Disable 2FA")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ---------- Setup dialog ---------- */}
      <Dialog open={setupOpen} onOpenChange={(v) => {
        // Don't allow closing while still on the recovery step until they ack
        if (!v && setupStep === "recovery") return;
        setSetupOpen(v);
      }}>
        <DialogContent data-testid="twofa-setup-dialog" className="max-w-md">
          {setupStep === "scan" && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t("twofa.setupTitle", "Scan with your authenticator")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    "twofa.setupDesc",
                    "Open your TOTP app, tap '+' / Add account, and scan this QR code. Then tap Continue to enter the first 6-digit code it shows."
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col items-center gap-3 py-2">
                <div className="rounded-md bg-white p-3" data-testid="twofa-qr">
                  <QRCodeSVG value={otpauthUri} size={192} level="M" />
                </div>
                <details className="text-xs w-full">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                    {t("twofa.cantScan", "Can't scan? Show secret manually")}
                  </summary>
                  <code className="block mt-2 p-2 rounded bg-muted text-foreground font-mono break-all text-center">
                    {secret}
                  </code>
                </details>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetupOpen(false)}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button onClick={() => setSetupStep("confirm")} data-testid="twofa-continue">
                  {t("common.continue", "Continue")}
                </Button>
              </DialogFooter>
            </>
          )}

          {setupStep === "confirm" && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t("twofa.confirmTitle", "Enter the 6-digit code")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    "twofa.confirmDesc",
                    "Type the current code from your authenticator app to finish enrolling."
                  )}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={confirmEnroll} className="space-y-3">
                <div>
                  <Label className="text-xs">{t("twofa.codeLabel", "6-digit code")}</Label>
                  <Input
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="font-mono text-center text-lg tracking-widest"
                    autoFocus
                    required
                    data-testid="twofa-confirm-code"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setSetupStep("scan")}>
                    {t("common.back", "Back")}
                  </Button>
                  <Button type="submit" disabled={busy || confirmCode.length !== 6} data-testid="twofa-confirm-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                    {t("twofa.confirmBtn", "Verify & enable")}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}

          {setupStep === "recovery" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-[hsl(var(--accent))]" />
                  {t("twofa.recoveryTitle", "Save your recovery codes")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    "twofa.recoveryDesc",
                    "These one-shot codes let you back into your account if you lose your phone. Store them in a password manager — we'll never show them again."
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-md bg-muted p-3 grid grid-cols-2 gap-1 text-xs font-mono" data-testid="twofa-recovery-list">
                {recoveryCodes.map((c) => (
                  <div key={c} className="px-2 py-1 select-all">{c}</div>
                ))}
              </div>
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertText>
                  {t(
                    "twofa.recoveryWarn",
                    "Each code works only once. Treat them like passwords."
                  )}
                </AlertText>
              </Alert>
              <DialogFooter className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={copyAllCodes} className="gap-1">
                  <Copy className="h-3.5 w-3.5" />
                  {t("twofa.copyBtn", "Copy")}
                </Button>
                <Button variant="outline" onClick={downloadCodes} className="gap-1">
                  <Download className="h-3.5 w-3.5" />
                  {t("twofa.downloadBtn", "Download .txt")}
                </Button>
                <Button onClick={() => { setSetupOpen(false); setSetupStep("scan"); }} data-testid="twofa-recovery-done">
                  {t("twofa.savedBtn", "I've saved them")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Disable dialog ---------- */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent data-testid="twofa-disable-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-4 w-4 text-[hsl(var(--danger))]" />
              {t("twofa.disableTitle", "Disable two-factor authentication")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "twofa.disableDesc",
                "We'll need both your password and a current 2FA code. Your account becomes less secure after this."
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={disable} className="space-y-3">
            <div>
              <Label className="text-xs">{t("twofa.password", "Password")}</Label>
              <PasswordInput
                value={disablePass}
                onChange={(e) => setDisablePass(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="twofa-disable-pass"
              />
            </div>
            <div>
              <Label className="text-xs">
                {t("twofa.codeOrRecovery", "Current 6-digit code or recovery code")}
              </Label>
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                placeholder="000000"
                className="font-mono"
                required
                data-testid="twofa-disable-code"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDisableOpen(false)}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={busy} variant="destructive" data-testid="twofa-disable-submit">
                {t("twofa.disableConfirm", "Disable 2FA")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Tiny inline alert text (the existing Alert component requires a title slot
// we don't always need)
function Alert({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warn))/0.3] bg-[hsl(var(--warn))/0.06] px-3 py-2 text-xs text-foreground mt-3">
      {children}
    </div>
  );
}

function AlertText({ children }) {
  return <span>{children}</span>;
}

export default TwoFactorCard;
